import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { SlidersHorizontal, LayoutGrid, Table2, Loader2, Search } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { LoadCard } from "@/components/LoadCard";
import { StatusPill } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";

const TRAILERS = ["Hotshot", "Expedited", "Gooseneck", "Flatbed", "Step Deck", "Dry Van", "Reefer", "Power Only", "Box Truck", "Sprinter Van", "Cargo Van", "Car Hauler"];

export default function FindLoads() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("cards");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ origin: "", destination: "", trailer_type: "", min_rpm: "", xd_exclusive: false });
  const [savedIds, setSavedIds] = useState([]);

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filters.origin) params.origin = filters.origin;
    if (filters.destination) params.destination = filters.destination;
    if (filters.trailer_type) params.trailer_type = filters.trailer_type;
    if (filters.min_rpm) params.min_rpm = filters.min_rpm;
    if (filters.xd_exclusive) params.xd_exclusive = true;
    try {
      const { data } = await api.get("/loads", { params });
      setLoads(data);
    } catch { toast.error("Failed to load marketplace"); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchLoads(); }, [fetchLoads]);
  useEffect(() => {
    if (user?.role === "carrier") api.get("/carrier/saved-loads").then((r) => setSavedIds(r.data.map((l) => l.id))).catch(() => {});
  }, [user]);

  const onSave = async (load) => {
    if (!user) { nav("/login"); return; }
    try {
      const { data } = await api.post(`/loads/${load.id}/save`);
      setSavedIds((s) => data.saved ? [...s, load.id] : s.filter((i) => i !== load.id));
      toast.success(data.saved ? "Load saved" : "Removed from saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const withSaved = loads.map((l) => ({ ...l, saved: savedIds.includes(l.id) }));

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-xd-orange">XD Freight Opportunities</span>
            <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">Find Loads</h1>
            <p className="mt-2 text-xd-muted">{loading ? "…" : `${withSaved.length} freight opportunities`} · AI-matched to your equipment & lanes. XD's dispatch team helps you book under your own authority.</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="toggle-filters" variant="outline" className="border-xd-border text-white hover:bg-xd-surface" onClick={() => setShowFilters(!showFilters)}>
              <SlidersHorizontal size={16} className="mr-2" /> Filters
            </Button>
            <div className="flex rounded-md border border-xd-border overflow-hidden">
              <button data-testid="view-cards" onClick={() => setView("cards")} className={`px-3 py-2 ${view === "cards" ? "bg-xd-orange text-white" : "text-xd-muted"}`}><LayoutGrid size={18} /></button>
              <button data-testid="view-table" onClick={() => setView("table")} className={`px-3 py-2 ${view === "table" ? "bg-xd-orange text-white" : "text-xd-muted"}`}><Table2 size={18} /></button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5 bg-xd-paper border border-xd-border rounded-lg p-5">
            <div><Label className="text-xd-muted text-xs">Origin</Label><Input data-testid="filter-origin" value={filters.origin} onChange={(e) => setFilters({ ...filters, origin: e.target.value })} placeholder="City or state" className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted text-xs">Destination</Label><Input data-testid="filter-destination" value={filters.destination} onChange={(e) => setFilters({ ...filters, destination: e.target.value })} placeholder="City" className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div>
              <Label className="text-xd-muted text-xs">Equipment</Label>
              <Select value={filters.trailer_type || "any"} onValueChange={(v) => setFilters({ ...filters, trailer_type: v === "any" ? "" : v })}>
                <SelectTrigger data-testid="filter-trailer" className="mt-1 bg-xd-surface border-xd-border"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent><SelectItem value="any">Any</SelectItem>{TRAILERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xd-muted text-xs">Min $/mi</Label><Input data-testid="filter-rpm" type="number" step="0.1" value={filters.min_rpm} onChange={(e) => setFilters({ ...filters, min_rpm: e.target.value })} placeholder="0.0" className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div className="flex items-end">
              <Button data-testid="apply-filters" className="w-full bg-xd-blue hover:bg-xd-blueH text-white" onClick={fetchLoads}><Search size={16} className="mr-2" /> Search</Button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div>
        ) : view === "cards" ? (
          <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {withSaved.map((l) => <LoadCard key={l.id} load={l} onSave={onSave} />)}
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-lg border border-xd-border">
            <table className="w-full text-sm">
              <thead className="bg-xd-surface text-xd-dim text-xs uppercase tracking-wide">
                <tr>{["XD #", "Lane", "Pickup", "Trailer", "Weight", "Miles", "Pay", "$/mi", ""].map((h) => <th key={h} className="text-left font-bold px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
              </thead>
              <tbody>
                {withSaved.map((l) => (
                  <tr key={l.id} data-testid={`load-row-${l.xd_number}`} className="border-t border-xd-border hover:bg-xd-surface transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-xd-dim">{l.xd_number}</td>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{l.origin_city}, {l.origin_state} → {l.dest_city}, {l.dest_state}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.pickup_date}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{l.trailer_type}</td>
                    <td className="px-4 py-3">{l.weight?.toLocaleString()}</td>
                    <td className="px-4 py-3">{l.loaded_miles}</td>
                    <td className="px-4 py-3 font-bold">${l.total_pay?.toLocaleString()}</td>
                    <td className="px-4 py-3 text-xd-blue font-semibold">${l.rate_per_mile}</td>
                    <td className="px-4 py-3"><Button asChild size="sm" className="bg-xd-orange hover:bg-xd-orangeH text-white"><a href={`/find-loads/${l.id}`}>View</a></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && withSaved.length === 0 && <p className="py-20 text-center text-xd-muted">No loads match your filters.</p>}
      </section>
    </PublicLayout>
  );
}
